export async function encryptMessage(
  message?: string,
  key?: CryptoKey
): Promise<string> {
  if (!key) {
    console.error("No crypto key available");
    return ""; // Or handle the error as you prefer
  }

  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encoder = new TextEncoder();
  const data = encoder.encode(message);

  const encrypted = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    data
  );

  const encryptedArray = new Uint8Array(encrypted);
  const combined = new Uint8Array(iv.length + encryptedArray.length);
  combined.set(iv);
  combined.set(encryptedArray, iv.length);

  return toBase64(combined);
}

export async function decryptMessage(
  base64Encoded: string,
  key: CryptoKey
): Promise<string> {
  const combined = fromBase64(base64Encoded);

  if (combined.length < 13) {
    throw new Error(
      "The provided data is too small to contain both IV and encrypted data"
    );
  }

  const iv = combined.slice(0, 12);
  const encrypted = combined.slice(12);

  try {
    const decrypted = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      encrypted
    );
    const decoder = new TextDecoder();
    return decoder.decode(decrypted);
  } catch (error) {
    console.error("Error during decryption:", error);
    throw error;
  }
}

export async function importKey(keyString?: string): Promise<CryptoKey> {
  if (keyString) {
    const rawKey = new TextEncoder().encode(keyString);
    if (rawKey.length !== 32) {
      throw new Error("Key length is not 256 bits");
    }

    return await window.crypto.subtle.importKey(
      "raw",
      rawKey,
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt", "decrypt"]
    );
  } else {
    throw new Error("No key provided");
  }
}

export function toBase64(buffer: Uint8Array): string {
  return btoa(String.fromCharCode.apply(null, buffer as unknown as number[]));
}

export function fromBase64(base64: string): Uint8Array {
  return Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
}
